import { useCallback, useEffect, useRef, useState } from "react";
import { createOrganization } from "@/lib/api";
import { TextInputField } from "@/components/common/TextInputField";
import { ConfirmButton } from "@/components/common/ConfirmButton";

interface CreateOrganizationDialogProps {
	open: boolean;
	onClose: () => void;
	onCreated: () => void;
}

export function CreateOrganizationDialog({
	open,
	onClose,
	onCreated,
}: CreateOrganizationDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [name, setName] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
		} else if (!open && el.open) {
			el.close();
		}
	}, [open]);

	useEffect(() => {
		if (open) {
			setName("");
			setError(null);
			setSubmitError(null);
			setLoading(false);
		}
	}, [open]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				e.preventDefault();
			}
		},
		[onClose],
	);

	function validate(): boolean {
		if (!name.trim()) {
			setError("組織名は必須です。");
			return false;
		}
		setError(null);
		return true;
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!validate()) return;
		setSubmitError(null);
		setLoading(true);
		try {
			await createOrganization(name.trim());
			onCreated();
			onClose();
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setLoading(false);
		}
	}

	if (!open) return null;

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			style={{
				margin: "auto",
				padding: 0,
				border: "none",
				borderRadius: "var(--radius-4)",
				boxShadow: "var(--shadow-card)",
				background: "var(--surface-1)",
				width: 480,
			}}
			className="backdrop:bg-scrim"
		>
			<form onSubmit={handleSubmit} noValidate>
				<div style={{ padding: "20px 24px 16px", borderBottom: "var(--pane-header-border)" }}>
					<h3
						style={{
							margin: 0,
							fontSize: "var(--text-title)",
							fontWeight: 600,
							color: "var(--fg-1)",
							fontFamily: "var(--font-sans)",
						}}
					>
						組織を作成
					</h3>
				</div>
				<div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
					{submitError && (
						<div
							style={{
								padding: "10px 14px",
								borderRadius: "var(--radius-3)",
								background: "var(--danger-subtle)",
								boxShadow: "0 0 0 1px var(--danger-line)",
								fontSize: "var(--text-ui)",
								color: "var(--danger)",
								fontFamily: "var(--font-sans)",
							}}
						>
							{submitError}
						</div>
					)}
					<TextInputField
						label="組織名"
						value={name}
						onChange={setName}
						placeholder="株式会社〇〇"
						autoComplete="off"
						error={error ?? undefined}
					/>
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
						borderTop: "var(--pane-header-border)",
						padding: "12px 24px",
					}}
				>
					<ConfirmButton
						variant="ghost"
						label="キャンセル"
						loading={false}
						disabled={loading}
						onClick={onClose}
					/>
					<ConfirmButton type="submit" label="作成" loading={loading} />
				</div>
			</form>
		</dialog>
	);
}
